import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import appLogo from '../assets/logo.svg';
import { PasswordStrengthMeter } from '../components/shared/PasswordStrengthMeter';
import { evaluatePassword } from '../lib/passwordStrength';

type Mode = 'invite' | 'recovery' | 'profile';

function detectMode(params: {
  mustSetPassword: boolean;
  passwordRecovery: boolean;
}): Mode {
  if (params.passwordRecovery) return 'recovery';
  if (params.mustSetPassword) return 'invite';
  return 'profile';
}

export function SetPasswordPage() {
  const navigate = useNavigate();
  const {
    session,
    user,
    loading: authLoading,
    mustSetPassword,
    passwordRecovery,
    setNewPassword,
    signOut,
  } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const mode: Mode = useMemo(
    () => detectMode({ mustSetPassword, passwordRecovery }),
    [mustSetPassword, passwordRecovery],
  );

  // Sans session, pas moyen de définir un mot de passe : on renvoie au login
  useEffect(() => {
    if (!authLoading && !session) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, session, navigate]);

  const strength = useMemo(() => evaluatePassword(password), [password]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!strength.valid) {
      setError(strength.firstError ?? 'Mot de passe trop faible.');
      return;
    }
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setSubmitting(true);
    const { error: err } = await setNewPassword(password);
    setSubmitting(false);
    if (err) {
      setError(err);
      return;
    }
    setSuccess(true);
    // Laisse 0.8s à l'utilisateur pour voir le succès, puis on va au dashboard
    setTimeout(() => navigate('/dashboard', { replace: true }), 800);
  };

  const title =
    mode === 'recovery'
      ? 'Réinitialiser votre mot de passe'
      : mode === 'invite'
        ? 'Bienvenue — définissez votre mot de passe'
        : 'Changer votre mot de passe';
  const description =
    mode === 'recovery'
      ? 'Choisissez un nouveau mot de passe pour accéder à votre compte.'
      : mode === 'invite'
        ? `Avant d'accéder à l'application, définissez un mot de passe pour votre compte ${user?.email ? `(${user.email})` : ''}.`
        : 'Mettez à jour votre mot de passe.';
  const submitLabel = mode === 'invite' ? 'Créer mon mot de passe' : 'Mettre à jour';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src={appLogo} alt="Logo" className="h-16 w-auto object-contain mb-4" />
          <h1 className="text-xl font-bold text-foreground">Prévisionnel Financier</h1>
          <p className="text-sm text-muted-foreground mt-1">My Notary — Outil interne</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-3">
                <Icon icon="lucide:check-circle-2" className="w-4 h-4 shrink-0" />
                Mot de passe enregistré. Redirection…
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pwd">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="pwd"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      autoFocus
                      className="pr-9"
                      disabled={submitting}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? 'Masquer' : 'Afficher'}
                    >
                      <Icon
                        icon={showPassword ? 'lucide:eye-off' : 'lucide:eye'}
                        className="w-4 h-4"
                      />
                    </button>
                  </div>
                  <PasswordStrengthMeter password={password} className="pt-1" />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="pwd-confirm">Confirmer</Label>
                  <Input
                    id="pwd-confirm"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                    disabled={submitting}
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-sm text-danger bg-danger/5 border border-danger/20 rounded-md px-3 py-2">
                    <Icon icon="lucide:alert-circle" className="w-4 h-4 shrink-0" />
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting || !strength.valid || password !== confirm}
                >
                  {submitting ? (
                    <>
                      <Icon icon="lucide:loader-2" className="w-4 h-4 animate-spin" />
                      Enregistrement…
                    </>
                  ) : (
                    <>
                      <Icon icon="lucide:check" className="w-4 h-4" />
                      {submitLabel}
                    </>
                  )}
                </Button>

                {mode !== 'invite' && (
                  <button
                    type="button"
                    onClick={async () => {
                      await signOut();
                      navigate('/login', { replace: true });
                    }}
                    className="w-full text-xs text-muted-foreground hover:text-foreground"
                  >
                    Annuler et se déconnecter
                  </button>
                )}
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
